import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Calculator } from 'lucide-react';

export const FinancialTools = () => {
  // SIP Calculator State
  const [sip, setSip] = useState({ monthly: 5000, years: 10, rate: 12 });
  const [sipResult, setSipResult] = useState(null);

  // Retirement Calculator State
  const [retirement, setRetirement] = useState({ currentAge: 30, retirementAge: 60, monthlyExpense: 50000, inflation: 6, returnRate: 12 });
  const [retirementResult, setRetirementResult] = useState(null);

  // EMI Calculator State
  const [emi, setEmi] = useState({ principal: 1000000, rate: 8.5, years: 10 });
  const [emiResult, setEmiResult] = useState(null);

  // Income Tax Calculator State
  const [tax, setTax] = useState({ income: 1000000, regime: 'old' });
  const [taxResult, setTaxResult] = useState(null);

  // SWP Calculator State
  const [swp, setSwp] = useState({ corpus: 1000000, monthly: 10000, years: 10, rate: 8 });
  const [swpResult, setSwpResult] = useState(null);

  // Step-Up SIP Calculator State
  const [stepSip, setStepSip] = useState({ initial: 5000, stepUp: 10, years: 10, rate: 12 });
  const [stepSipResult, setStepSipResult] = useState(null);

  // HRA Calculator State
  const [hra, setHra] = useState({ salary: 50000, hraReceived: 15000, rentPaid: 20000, metro: 'yes' });
  const [hraResult, setHraResult] = useState(null);

  // Capital Gain Calculator State
  const [capGain, setCapGain] = useState({ buyPrice: 100000, sellPrice: 150000, type: 'STCG' });
  const [capGainResult, setCapGainResult] = useState(null);

  // Tax Saving Calculator State
  const [taxSaving, setTaxSaving] = useState({ section80C: 150000, section80D: 25000, section80CCD: 50000 });
  const [taxSavingResult, setTaxSavingResult] = useState(null);

  // GST Calculator State
  const [gst, setGst] = useState({ amount: 10000, gstRate: 18, type: 'excluding' });
  const [gstResult, setGstResult] = useState(null);

  // TDS Calculator State
  const [tds, setTds] = useState({ tdsAmount: 10000, daysDelay: 30 });
  const [tdsResult, setTdsResult] = useState(null);

  // FIRE Calculator State
  const [fire, setFire] = useState({ currentExpenses: 50000, currentAge: 30, fireAge: 50, inflation: 6, returnRate: 12 });
  const [fireResult, setFireResult] = useState(null);

  // Future Value Calculator State
  const [fv, setFv] = useState({ present: 100000, rate: 10, years: 5 });
  const [fvResult, setFvResult] = useState(null);

  // Child Marriage Calculator State
  const [childMarriage, setChildMarriage] = useState({ childAge: 5, marriageAge: 25, targetAmount: 2000000, rate: 12 });
  const [childMarriageResult, setChildMarriageResult] = useState(null);

  // Calculate Functions
  const calculateSIP = () => {
    const { monthly, years, rate } = sip;
    const months = years * 12;
    const monthlyRate = rate / 12 / 100;
    const futureValue = monthly * (((Math.pow(1 + monthlyRate, months) - 1) / monthlyRate) * (1 + monthlyRate));
    const invested = monthly * months;
    const returns = futureValue - invested;
    setSipResult({ futureValue: Math.round(futureValue), invested, returns: Math.round(returns) });
  };

  const calculateRetirement = () => {
    const { currentAge, retirementAge, monthlyExpense, inflation, returnRate } = retirement;
    const years = retirementAge - currentAge;
    const futureExpense = monthlyExpense * Math.pow(1 + inflation / 100, years);
    const corpusRequired = (futureExpense * 12 * 25);
    const monthlySIP = (corpusRequired * (returnRate / 12 / 100)) / (Math.pow(1 + returnRate / 12 / 100, years * 12) - 1);
    setRetirementResult({
      corpusRequired: Math.round(corpusRequired),
      monthlySIP: Math.round(monthlySIP),
      futureExpense: Math.round(futureExpense)
    });
  };

  const calculateEMI = () => {
    const { principal, rate, years } = emi;
    const months = years * 12;
    const monthlyRate = rate / 12 / 100;
    const emiValue = principal * monthlyRate * Math.pow(1 + monthlyRate, months) / (Math.pow(1 + monthlyRate, months) - 1);
    const totalPayment = emiValue * months;
    const interest = totalPayment - principal;
    setEmiResult({ emi: Math.round(emiValue), total: Math.round(totalPayment), interest: Math.round(interest) });
  };

  const calculateIncomeTax = () => {
    const { income, regime } = tax;
    let taxAmount = 0;
    
    if (regime === 'old') {
      if (income <= 250000) taxAmount = 0;
      else if (income <= 500000) taxAmount = (income - 250000) * 0.05;
      else if (income <= 1000000) taxAmount = 12500 + (income - 500000) * 0.20;
      else taxAmount = 112500 + (income - 1000000) * 0.30;
    } else {
      if (income <= 300000) taxAmount = 0;
      else if (income <= 600000) taxAmount = (income - 300000) * 0.05;
      else if (income <= 900000) taxAmount = 15000 + (income - 600000) * 0.10;
      else if (income <= 1200000) taxAmount = 45000 + (income - 900000) * 0.15;
      else if (income <= 1500000) taxAmount = 90000 + (income - 1200000) * 0.20;
      else taxAmount = 150000 + (income - 1500000) * 0.30;
    }
    
    const cess = taxAmount * 0.04;
    setTaxResult({ tax: Math.round(taxAmount), cess: Math.round(cess), total: Math.round(taxAmount + cess) });
  };

  const calculateSWP = () => {
    const { corpus, monthly, years, rate } = swp;
    const months = years * 12;
    const monthlyRate = rate / 12 / 100;
    const finalCorpus = corpus * Math.pow(1 + monthlyRate, months) - monthly * ((Math.pow(1 + monthlyRate, months) - 1) / monthlyRate);
    const withdrawn = monthly * months;
    setSwpResult({ finalCorpus: Math.round(finalCorpus), withdrawn, totalReturns: Math.round(finalCorpus + withdrawn - corpus) });
  };

  const calculateStepSIP = () => {
    const { initial, stepUp, years, rate } = stepSip;
    let totalInvested = 0;
    let futureValue = 0;
    const monthlyRate = rate / 12 / 100;
    
    for (let year = 0; year < years; year++) {
      const monthlyAmount = initial * Math.pow(1 + stepUp / 100, year);
      const yearInvestment = monthlyAmount * 12;
      totalInvested += yearInvestment;
      const monthsRemaining = (years - year) * 12;
      futureValue += yearInvestment * Math.pow(1 + monthlyRate, monthsRemaining);
    }
    
    setStepSipResult({ futureValue: Math.round(futureValue), invested: Math.round(totalInvested), returns: Math.round(futureValue - totalInvested) });
  };

  const calculateHRA = () => {
    const { salary, hraReceived, rentPaid, metro } = hra;
    const annualSalary = salary * 12;
    const annualHRA = hraReceived * 12;
    const annualRent = rentPaid * 12;
    const metroPercent = metro === 'yes' ? 0.5 : 0.4;
    
    const option1 = annualHRA;
    const option2 = annualRent - (annualSalary * 0.1);
    const option3 = annualSalary * metroPercent;
    
    const exemption = Math.min(option1, option2, option3);
    const taxable = annualHRA - exemption;
    
    setHraResult({ exemption: Math.round(exemption), taxable: Math.round(Math.max(0, taxable)) });
  };

  const calculateCapitalGain = () => {
    const { buyPrice, sellPrice, type } = capGain;
    const gain = sellPrice - buyPrice;
    let tax = 0;
    
    if (type === 'STCG') {
      tax = gain * 0.15;
    } else {
      tax = gain * 0.10;
    }
    
    setCapGainResult({ gain, tax: Math.round(tax), netGain: Math.round(gain - tax) });
  };

  const calculateTaxSaving = () => {
    const { section80C, section80D, section80CCD } = taxSaving;
    const total80C = Math.min(section80C, 150000);
    const total80D = Math.min(section80D, 25000);
    const total80CCD = Math.min(section80CCD, 50000);
    const totalDeduction = total80C + total80D + total80CCD;
    const taxSaved = totalDeduction * 0.30;
    
    setTaxSavingResult({ totalDeduction, taxSaved: Math.round(taxSaved) });
  };

  const calculateGST = () => {
    const { amount, gstRate, type } = gst;
    let result = {};
    
    if (type === 'excluding') {
      const gstAmount = (amount * gstRate) / 100;
      result = { gstAmount: Math.round(gstAmount), total: Math.round(amount + gstAmount), base: amount };
    } else {
      const base = (amount * 100) / (100 + gstRate);
      const gstAmount = amount - base;
      result = { gstAmount: Math.round(gstAmount), total: amount, base: Math.round(base) };
    }
    
    setGstResult(result);
  };

  const calculateTDS = () => {
    const { tdsAmount, daysDelay } = tds;
    const interestRate = 0.015;
    const interest = (tdsAmount * interestRate * daysDelay) / 30;
    const lateFee = daysDelay > 0 ? 200 : 0;
    
    setTdsResult({ interest: Math.round(interest), lateFee, total: Math.round(tdsAmount + interest + lateFee) });
  };

  const calculateFIRE = () => {
    const { currentExpenses, currentAge, fireAge, inflation, returnRate } = fire;
    const years = fireAge - currentAge;
    const futureExpenses = currentExpenses * Math.pow(1 + inflation / 100, years);
    const corpusRequired = (futureExpenses * 12 * 25);
    const monthlySIP = (corpusRequired * (returnRate / 12 / 100)) / (Math.pow(1 + returnRate / 12 / 100, years * 12) - 1);
    
    setFireResult({ corpusRequired: Math.round(corpusRequired), monthlySIP: Math.round(monthlySIP), futureExpenses: Math.round(futureExpenses) });
  };

  const calculateFutureValue = () => {
    const { present, rate, years } = fv;
    const future = present * Math.pow(1 + rate / 100, years);
    const returns = future - present;
    
    setFvResult({ futureValue: Math.round(future), returns: Math.round(returns) });
  };

  const calculateChildMarriage = () => {
    const { childAge, marriageAge, targetAmount, rate } = childMarriage;
    const years = marriageAge - childAge;
    const months = years * 12;
    const monthlyRate = rate / 12 / 100;
    const monthlySIP = (targetAmount * monthlyRate) / (Math.pow(1 + monthlyRate, months) - 1);
    const totalInvestment = monthlySIP * months;
    
    setChildMarriageResult({ monthlySIP: Math.round(monthlySIP), totalInvestment: Math.round(totalInvestment), years });
  };

  return (
    <div className="bg-background py-24 md:py-32" data-testid="financial-tools-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <p className="text-sm font-medium tracking-wide uppercase text-secondary mb-4">Financial Tools</p>
          <h1 className="text-5xl md:text-6xl font-bold tracking-tight leading-tight font-playfair mb-6" data-testid="tools-title">
            Smart Financial Calculators
          </h1>
          <p className="text-lg md:text-xl leading-relaxed text-muted-foreground max-w-3xl mx-auto">
            Plan your investments, taxes, and financial goals with our comprehensive suite of calculators.
          </p>
        </div>

        <Tabs defaultValue="sip" className="space-y-8">
          <TabsList className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2 h-auto bg-transparent">
            <TabsTrigger value="sip" className="data-[state=active]:bg-secondary data-[state=active]:text-white">SIP</TabsTrigger>
            <TabsTrigger value="retirement" className="data-[state=active]:bg-secondary data-[state=active]:text-white">Retirement</TabsTrigger>
            <TabsTrigger value="emi" className="data-[state=active]:bg-secondary data-[state=active]:text-white">EMI</TabsTrigger>
            <TabsTrigger value="tax" className="data-[state=active]:bg-secondary data-[state=active]:text-white">Tax</TabsTrigger>
            <TabsTrigger value="swp" className="data-[state=active]:bg-secondary data-[state=active]:text-white">SWP</TabsTrigger>
            <TabsTrigger value="step-sip" className="data-[state=active]:bg-secondary data-[state=active]:text-white">Step-Up SIP</TabsTrigger>
            <TabsTrigger value="hra" className="data-[state=active]:bg-secondary data-[state=active]:text-white">HRA</TabsTrigger>
            <TabsTrigger value="capital-gain" className="data-[state=active]:bg-secondary data-[state=active]:text-white">Capital Gain</TabsTrigger>
            <TabsTrigger value="tax-saving" className="data-[state=active]:bg-secondary data-[state=active]:text-white">Tax Saving</TabsTrigger>
            <TabsTrigger value="gst" className="data-[state=active]:bg-secondary data-[state=active]:text-white">GST</TabsTrigger>
            <TabsTrigger value="tds" className="data-[state=active]:bg-secondary data-[state=active]:text-white">TDS</TabsTrigger>
            <TabsTrigger value="fire" className="data-[state=active]:bg-secondary data-[state=active]:text-white">FIRE</TabsTrigger>
            <TabsTrigger value="fv" className="data-[state=active]:bg-secondary data-[state=active]:text-white">Future Value</TabsTrigger>
            <TabsTrigger value="child-marriage" className="data-[state=active]:bg-secondary data-[state=active]:text-white">Child Marriage</TabsTrigger>
          </TabsList>

          {/* SIP Calculator */}
          <TabsContent value="sip" data-testid="sip-calculator">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calculator className="h-5 w-5" />
                  SIP Calculator
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label>Monthly Investment (₹)</Label>
                    <Input type="number" value={sip.monthly} onChange={(e) => setSip({...sip, monthly: Number(e.target.value)})} />
                  </div>
                  <div>
                    <Label>Investment Period (Years)</Label>
                    <Input type="number" value={sip.years} onChange={(e) => setSip({...sip, years: Number(e.target.value)})} />
                  </div>
                  <div>
                    <Label>Expected Return (%)</Label>
                    <Input type="number" value={sip.rate} onChange={(e) => setSip({...sip, rate: Number(e.target.value)})} />
                  </div>
                </div>
                <Button onClick={calculateSIP} className="w-full md:w-auto">Calculate</Button>
                {sipResult && (
                  <div className="bg-muted/50 p-6 rounded-lg space-y-2">
                    <div className="flex justify-between"><span>Total Investment:</span><span className="font-semibold">₹{sipResult.invested.toLocaleString('en-IN')}</span></div>
                    <div className="flex justify-between"><span>Estimated Returns:</span><span className="font-semibold text-secondary">₹{sipResult.returns.toLocaleString('en-IN')}</span></div>
                    <div className="flex justify-between text-lg font-bold"><span>Future Value:</span><span className="text-secondary">₹{sipResult.futureValue.toLocaleString('en-IN')}</span></div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Retirement Calculator */}
          <TabsContent value="retirement" data-testid="retirement-calculator">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calculator className="h-5 w-5" />
                  Retirement Planning Calculator
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label>Current Age</Label>
                    <Input type="number" value={retirement.currentAge} onChange={(e) => setRetirement({...retirement, currentAge: Number(e.target.value)})} />
                  </div>
                  <div>
                    <Label>Retirement Age</Label>
                    <Input type="number" value={retirement.retirementAge} onChange={(e) => setRetirement({...retirement, retirementAge: Number(e.target.value)})} />
                  </div>
                  <div>
                    <Label>Monthly Expense (₹)</Label>
                    <Input type="number" value={retirement.monthlyExpense} onChange={(e) => setRetirement({...retirement, monthlyExpense: Number(e.target.value)})} />
                  </div>
                  <div>
                    <Label>Inflation Rate (%)</Label>
                    <Input type="number" value={retirement.inflation} onChange={(e) => setRetirement({...retirement, inflation: Number(e.target.value)})} />
                  </div>
                  <div>
                    <Label>Expected Return (%)</Label>
                    <Input type="number" value={retirement.returnRate} onChange={(e) => setRetirement({...retirement, returnRate: Number(e.target.value)})} />
                  </div>
                </div>
                <Button onClick={calculateRetirement} className="w-full md:w-auto">Calculate</Button>
                {retirementResult && (
                  <div className="bg-muted/50 p-6 rounded-lg space-y-2">
                    <div className="flex justify-between"><span>Corpus Required:</span><span className="font-semibold">₹{retirementResult.corpusRequired.toLocaleString('en-IN')}</span></div>
                    <div className="flex justify-between"><span>Monthly SIP Needed:</span><span className="font-semibold text-secondary">₹{retirementResult.monthlySIP.toLocaleString('en-IN')}</span></div>
                    <div className="flex justify-between"><span>Future Monthly Expense:</span><span className="font-semibold">₹{retirementResult.futureExpense.toLocaleString('en-IN')}</span></div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* EMI Calculator */}
          <TabsContent value="emi" data-testid="emi-calculator">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calculator className="h-5 w-5" />
                  EMI Calculator
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label>Loan Amount (₹)</Label>
                    <Input type="number" value={emi.principal} onChange={(e) => setEmi({...emi, principal: Number(e.target.value)})} />
                  </div>
                  <div>
                    <Label>Interest Rate (% p.a.)</Label>
                    <Input type="number" step="0.1" value={emi.rate} onChange={(e) => setEmi({...emi, rate: Number(e.target.value)})} />
                  </div>
                  <div>
                    <Label>Loan Tenure (Years)</Label>
                    <Input type="number" value={emi.years} onChange={(e) => setEmi({...emi, years: Number(e.target.value)})} />
                  </div>
                </div>
                <Button onClick={calculateEMI} className="w-full md:w-auto">Calculate EMI</Button>
                {emiResult && (
                  <div className="bg-muted/50 p-6 rounded-lg space-y-2">
                    <div className="flex justify-between"><span>Monthly EMI:</span><span className="font-semibold text-secondary text-lg">₹{emiResult.emi.toLocaleString('en-IN')}</span></div>
                    <div className="flex justify-between"><span>Principal Amount:</span><span className="font-semibold">₹{emi.principal.toLocaleString('en-IN')}</span></div>
                    <div className="flex justify-between"><span>Total Interest:</span><span className="font-semibold">₹{emiResult.interest.toLocaleString('en-IN')}</span></div>
                    <div className="flex justify-between text-lg font-bold"><span>Total Payment:</span><span>₹{emiResult.total.toLocaleString('en-IN')}</span></div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Income Tax Calculator */}
          <TabsContent value="tax" data-testid="tax-calculator">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calculator className="h-5 w-5" />
                  Income Tax Calculator
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Annual Income (₹)</Label>
                    <Input type="number" value={tax.income} onChange={(e) => setTax({...tax, income: Number(e.target.value)})} />
                  </div>
                  <div>
                    <Label>Tax Regime</Label>
                    <Select value={tax.regime} onValueChange={(value) => setTax({...tax, regime: value})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="old">Old Regime</SelectItem>
                        <SelectItem value="new">New Regime</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <Button onClick={calculateIncomeTax} className="w-full md:w-auto">Calculate Tax</Button>
                {taxResult && (
                  <div className="bg-muted/50 p-6 rounded-lg space-y-2">
                    <div className="flex justify-between"><span>Income Tax:</span><span className="font-semibold">₹{taxResult.tax.toLocaleString('en-IN')}</span></div>
                    <div className="flex justify-between"><span>Health & Education Cess (4%):</span><span className="font-semibold">₹{taxResult.cess.toLocaleString('en-IN')}</span></div>
                    <div className="flex justify-between text-lg font-bold"><span>Total Tax:</span><span className="text-red-600">₹{taxResult.total.toLocaleString('en-IN')}</span></div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* SWP Calculator */}
          <TabsContent value="swp" data-testid="swp-calculator">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calculator className="h-5 w-5" />
                  Systematic Withdrawal Plan (SWP) Calculator
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Total Corpus (₹)</Label>
                    <Input type="number" value={swp.corpus} onChange={(e) => setSwp({...swp, corpus: Number(e.target.value)})} />
                  </div>
                  <div>
                    <Label>Monthly Withdrawal (₹)</Label>
                    <Input type="number" value={swp.monthly} onChange={(e) => setSwp({...swp, monthly: Number(e.target.value)})} />
                  </div>
                  <div>
                    <Label>Withdrawal Period (Years)</Label>
                    <Input type="number" value={swp.years} onChange={(e) => setSwp({...swp, years: Number(e.target.value)})} />
                  </div>
                  <div>
                    <Label>Expected Return (% p.a.)</Label>
                    <Input type="number" value={swp.rate} onChange={(e) => setSwp({...swp, rate: Number(e.target.value)})} />
                  </div>
                </div>
                <Button onClick={calculateSWP} className="w-full md:w-auto">Calculate</Button>
                {swpResult && (
                  <div className="bg-muted/50 p-6 rounded-lg space-y-2">
                    <div className="flex justify-between"><span>Total Withdrawn:</span><span className="font-semibold">₹{swpResult.withdrawn.toLocaleString('en-IN')}</span></div>
                    <div className="flex justify-between"><span>Final Corpus:</span><span className="font-semibold text-secondary">₹{swpResult.finalCorpus.toLocaleString('en-IN')}</span></div>
                    <div className="flex justify-between"><span>Total Returns:</span><span className="font-semibold">₹{swpResult.totalReturns.toLocaleString('en-IN')}</span></div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Step-Up SIP Calculator */}
          <TabsContent value="step-sip" data-testid="step-sip-calculator">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calculator className="h-5 w-5" />
                  Step-Up SIP Calculator
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Initial Monthly Investment (₹)</Label>
                    <Input type="number" value={stepSip.initial} onChange={(e) => setStepSip({...stepSip, initial: Number(e.target.value)})} />
                  </div>
                  <div>
                    <Label>Annual Step-Up (%)</Label>
                    <Input type="number" value={stepSip.stepUp} onChange={(e) => setStepSip({...stepSip, stepUp: Number(e.target.value)})} />
                  </div>
                  <div>
                    <Label>Investment Period (Years)</Label>
                    <Input type="number" value={stepSip.years} onChange={(e) => setStepSip({...stepSip, years: Number(e.target.value)})} />
                  </div>
                  <div>
                    <Label>Expected Return (%)</Label>
                    <Input type="number" value={stepSip.rate} onChange={(e) => setStepSip({...stepSip, rate: Number(e.target.value)})} />
                  </div>
                </div>
                <Button onClick={calculateStepSIP} className="w-full md:w-auto">Calculate</Button>
                {stepSipResult && (
                  <div className="bg-muted/50 p-6 rounded-lg space-y-2">
                    <div className="flex justify-between"><span>Total Investment:</span><span className="font-semibold">₹{stepSipResult.invested.toLocaleString('en-IN')}</span></div>
                    <div className="flex justify-between"><span>Estimated Returns:</span><span className="font-semibold text-secondary">₹{stepSipResult.returns.toLocaleString('en-IN')}</span></div>
                    <div className="flex justify-between text-lg font-bold"><span>Future Value:</span><span className="text-secondary">₹{stepSipResult.futureValue.toLocaleString('en-IN')}</span></div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* HRA Exemption Calculator */}
          <TabsContent value="hra" data-testid="hra-calculator">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calculator className="h-5 w-5" />
                  HRA Exemption Calculator
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Basic Salary (Monthly) (₹)</Label>
                    <Input type="number" value={hra.salary} onChange={(e) => setHra({...hra, salary: Number(e.target.value)})} />
                  </div>
                  <div>
                    <Label>HRA Received (Monthly) (₹)</Label>
                    <Input type="number" value={hra.hraReceived} onChange={(e) => setHra({...hra, hraReceived: Number(e.target.value)})} />
                  </div>
                  <div>
                    <Label>Rent Paid (Monthly) (₹)</Label>
                    <Input type="number" value={hra.rentPaid} onChange={(e) => setHra({...hra, rentPaid: Number(e.target.value)})} />
                  </div>
                  <div>
                    <Label>Living in Metro City?</Label>
                    <Select value={hra.metro} onValueChange={(value) => setHra({...hra, metro: value})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="yes">Yes</SelectItem>
                        <SelectItem value="no">No</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <Button onClick={calculateHRA} className="w-full md:w-auto">Calculate</Button>
                {hraResult && (
                  <div className="bg-muted/50 p-6 rounded-lg space-y-2">
                    <div className="flex justify-between"><span>HRA Exemption (Annual):</span><span className="font-semibold text-secondary">₹{hraResult.exemption.toLocaleString('en-IN')}</span></div>
                    <div className="flex justify-between"><span>Taxable HRA (Annual):</span><span className="font-semibold">₹{hraResult.taxable.toLocaleString('en-IN')}</span></div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Capital Gain Calculator */}
          <TabsContent value="capital-gain" data-testid="capital-gain-calculator">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calculator className="h-5 w-5" />
                  Capital Gain Calculator
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label>Purchase Price (₹)</Label>
                    <Input type="number" value={capGain.buyPrice} onChange={(e) => setCapGain({...capGain, buyPrice: Number(e.target.value)})} />
                  </div>
                  <div>
                    <Label>Sale Price (₹)</Label>
                    <Input type="number" value={capGain.sellPrice} onChange={(e) => setCapGain({...capGain, sellPrice: Number(e.target.value)})} />
                  </div>
                  <div>
                    <Label>Type</Label>
                    <Select value={capGain.type} onValueChange={(value) => setCapGain({...capGain, type: value})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="STCG">Short Term Capital Gain (15%)</SelectItem>
                        <SelectItem value="LTCG">Long Term Capital Gain (10%)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <Button onClick={calculateCapitalGain} className="w-full md:w-auto">Calculate</Button>
                {capGainResult && (
                  <div className="bg-muted/50 p-6 rounded-lg space-y-2">
                    <div className="flex justify-between"><span>Capital Gain:</span><span className="font-semibold">₹{capGainResult.gain.toLocaleString('en-IN')}</span></div>
                    <div className="flex justify-between"><span>Tax on Gain:</span><span className="font-semibold text-red-600">₹{capGainResult.tax.toLocaleString('en-IN')}</span></div>
                    <div className="flex justify-between text-lg font-bold"><span>Net Gain:</span><span className="text-secondary">₹{capGainResult.netGain.toLocaleString('en-IN')}</span></div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tax Saving Calculator */}
          <TabsContent value="tax-saving" data-testid="tax-saving-calculator">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calculator className="h-5 w-5" />
                  Tax Saving Calculator (80C, 80D, 80CCD)
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label>Section 80C Investment (₹)</Label>
                    <Input type="number" value={taxSaving.section80C} onChange={(e) => setTaxSaving({...taxSaving, section80C: Number(e.target.value)})} placeholder="Max ₹1,50,000" />
                  </div>
                  <div>
                    <Label>Section 80D (Health Insurance) (₹)</Label>
                    <Input type="number" value={taxSaving.section80D} onChange={(e) => setTaxSaving({...taxSaving, section80D: Number(e.target.value)})} placeholder="Max ₹25,000" />
                  </div>
                  <div>
                    <Label>Section 80CCD(1B) (NPS) (₹)</Label>
                    <Input type="number" value={taxSaving.section80CCD} onChange={(e) => setTaxSaving({...taxSaving, section80CCD: Number(e.target.value)})} placeholder="Max ₹50,000" />
                  </div>
                </div>
                <Button onClick={calculateTaxSaving} className="w-full md:w-auto">Calculate</Button>
                {taxSavingResult && (
                  <div className="bg-muted/50 p-6 rounded-lg space-y-2">
                    <div className="flex justify-between"><span>Total Deductions:</span><span className="font-semibold">₹{taxSavingResult.totalDeduction.toLocaleString('en-IN')}</span></div>
                    <div className="flex justify-between text-lg font-bold"><span>Tax Saved (30% slab):</span><span className="text-secondary">₹{taxSavingResult.taxSaved.toLocaleString('en-IN')}</span></div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* GST Calculator */}
          <TabsContent value="gst" data-testid="gst-calculator">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calculator className="h-5 w-5" />
                  GST Calculator
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label>Amount (₹)</Label>
                    <Input type="number" value={gst.amount} onChange={(e) => setGst({...gst, amount: Number(e.target.value)})} />
                  </div>
                  <div>
                    <Label>GST Rate (%)</Label>
                    <Select value={gst.gstRate.toString()} onValueChange={(value) => setGst({...gst, gstRate: Number(value)})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="5">5%</SelectItem>
                        <SelectItem value="12">12%</SelectItem>
                        <SelectItem value="18">18%</SelectItem>
                        <SelectItem value="28">28%</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>GST Type</Label>
                    <Select value={gst.type} onValueChange={(value) => setGst({...gst, type: value})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="excluding">Excluding GST</SelectItem>
                        <SelectItem value="including">Including GST</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <Button onClick={calculateGST} className="w-full md:w-auto">Calculate</Button>
                {gstResult && (
                  <div className="bg-muted/50 p-6 rounded-lg space-y-2">
                    <div className="flex justify-between"><span>Base Amount:</span><span className="font-semibold">₹{gstResult.base.toLocaleString('en-IN')}</span></div>
                    <div className="flex justify-between"><span>GST Amount:</span><span className="font-semibold">₹{gstResult.gstAmount.toLocaleString('en-IN')}</span></div>
                    <div className="flex justify-between text-lg font-bold"><span>Total Amount:</span><span className="text-secondary">₹{gstResult.total.toLocaleString('en-IN')}</span></div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* TDS Calculator */}
          <TabsContent value="tds" data-testid="tds-calculator">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calculator className="h-5 w-5" />
                  TDS Interest & Late Fee Calculator
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>TDS Amount (₹)</Label>
                    <Input type="number" value={tds.tdsAmount} onChange={(e) => setTds({...tds, tdsAmount: Number(e.target.value)})} />
                  </div>
                  <div>
                    <Label>Days Delayed</Label>
                    <Input type="number" value={tds.daysDelay} onChange={(e) => setTds({...tds, daysDelay: Number(e.target.value)})} />
                  </div>
                </div>
                <Button onClick={calculateTDS} className="w-full md:w-auto">Calculate</Button>
                {tdsResult && (
                  <div className="bg-muted/50 p-6 rounded-lg space-y-2">
                    <div className="flex justify-between"><span>TDS Amount:</span><span className="font-semibold">₹{tds.tdsAmount.toLocaleString('en-IN')}</span></div>
                    <div className="flex justify-between"><span>Interest (1.5% per month):</span><span className="font-semibold">₹{tdsResult.interest.toLocaleString('en-IN')}</span></div>
                    <div className="flex justify-between"><span>Late Fee:</span><span className="font-semibold">₹{tdsResult.lateFee.toLocaleString('en-IN')}</span></div>
                    <div className="flex justify-between text-lg font-bold"><span>Total Payable:</span><span className="text-red-600">₹{tdsResult.total.toLocaleString('en-IN')}</span></div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* FIRE Calculator */}
          <TabsContent value="fire" data-testid="fire-calculator">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calculator className="h-5 w-5" />
                  FIRE Calculator (Financial Independence, Retire Early)
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label>Current Monthly Expenses (₹)</Label>
                    <Input type="number" value={fire.currentExpenses} onChange={(e) => setFire({...fire, currentExpenses: Number(e.target.value)})} />
                  </div>
                  <div>
                    <Label>Current Age</Label>
                    <Input type="number" value={fire.currentAge} onChange={(e) => setFire({...fire, currentAge: Number(e.target.value)})} />
                  </div>
                  <div>
                    <Label>FIRE Age</Label>
                    <Input type="number" value={fire.fireAge} onChange={(e) => setFire({...fire, fireAge: Number(e.target.value)})} />
                  </div>
                  <div>
                    <Label>Inflation Rate (%)</Label>
                    <Input type="number" value={fire.inflation} onChange={(e) => setFire({...fire, inflation: Number(e.target.value)})} />
                  </div>
                  <div>
                    <Label>Expected Return (%)</Label>
                    <Input type="number" value={fire.returnRate} onChange={(e) => setFire({...fire, returnRate: Number(e.target.value)})} />
                  </div>
                </div>
                <Button onClick={calculateFIRE} className="w-full md:w-auto">Calculate</Button>
                {fireResult && (
                  <div className="bg-muted/50 p-6 rounded-lg space-y-2">
                    <div className="flex justify-between"><span>Corpus Required:</span><span className="font-semibold">₹{fireResult.corpusRequired.toLocaleString('en-IN')}</span></div>
                    <div className="flex justify-between"><span>Monthly SIP Needed:</span><span className="font-semibold text-secondary">₹{fireResult.monthlySIP.toLocaleString('en-IN')}</span></div>
                    <div className="flex justify-between"><span>Future Monthly Expenses:</span><span className="font-semibold">₹{fireResult.futureExpenses.toLocaleString('en-IN')}</span></div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Future Value Calculator */}
          <TabsContent value="fv" data-testid="future-value-calculator">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calculator className="h-5 w-5" />
                  Future Value Calculator
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label>Present Value (₹)</Label>
                    <Input type="number" value={fv.present} onChange={(e) => setFv({...fv, present: Number(e.target.value)})} />
                  </div>
                  <div>
                    <Label>Interest Rate (% p.a.)</Label>
                    <Input type="number" value={fv.rate} onChange={(e) => setFv({...fv, rate: Number(e.target.value)})} />
                  </div>
                  <div>
                    <Label>Time Period (Years)</Label>
                    <Input type="number" value={fv.years} onChange={(e) => setFv({...fv, years: Number(e.target.value)})} />
                  </div>
                </div>
                <Button onClick={calculateFutureValue} className="w-full md:w-auto">Calculate</Button>
                {fvResult && (
                  <div className="bg-muted/50 p-6 rounded-lg space-y-2">
                    <div className="flex justify-between"><span>Present Value:</span><span className="font-semibold">₹{fv.present.toLocaleString('en-IN')}</span></div>
                    <div className="flex justify-between"><span>Returns:</span><span className="font-semibold text-secondary">₹{fvResult.returns.toLocaleString('en-IN')}</span></div>
                    <div className="flex justify-between text-lg font-bold"><span>Future Value:</span><span className="text-secondary">₹{fvResult.futureValue.toLocaleString('en-IN')}</span></div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Child Marriage Calculator */}
          <TabsContent value="child-marriage" data-testid="child-marriage-calculator">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calculator className="h-5 w-5" />
                  Child Marriage Planning Calculator
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Child's Current Age</Label>
                    <Input type="number" value={childMarriage.childAge} onChange={(e) => setChildMarriage({...childMarriage, childAge: Number(e.target.value)})} />
                  </div>
                  <div>
                    <Label>Expected Marriage Age</Label>
                    <Input type="number" value={childMarriage.marriageAge} onChange={(e) => setChildMarriage({...childMarriage, marriageAge: Number(e.target.value)})} />
                  </div>
                  <div>
                    <Label>Target Amount (₹)</Label>
                    <Input type="number" value={childMarriage.targetAmount} onChange={(e) => setChildMarriage({...childMarriage, targetAmount: Number(e.target.value)})} />
                  </div>
                  <div>
                    <Label>Expected Return (%)</Label>
                    <Input type="number" value={childMarriage.rate} onChange={(e) => setChildMarriage({...childMarriage, rate: Number(e.target.value)})} />
                  </div>
                </div>
                <Button onClick={calculateChildMarriage} className="w-full md:w-auto">Calculate</Button>
                {childMarriageResult && (
                  <div className="bg-muted/50 p-6 rounded-lg space-y-2">
                    <div className="flex justify-between"><span>Investment Period:</span><span className="font-semibold">{childMarriageResult.years} years</span></div>
                    <div className="flex justify-between"><span>Monthly SIP Required:</span><span className="font-semibold text-secondary text-lg">₹{childMarriageResult.monthlySIP.toLocaleString('en-IN')}</span></div>
                    <div className="flex justify-between"><span>Total Investment:</span><span className="font-semibold">₹{childMarriageResult.totalInvestment.toLocaleString('en-IN')}</span></div>
                    <div className="flex justify-between text-lg font-bold"><span>Target Amount:</span><span>₹{childMarriage.targetAmount.toLocaleString('en-IN')}</span></div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default FinancialTools;
